import * as React from "react";
import s from "./index.module.scss";
import * as Assets from "../../assets";

interface IFullscreenControl {
	fullscreenRef: React.RefObject<HTMLDivElement>;
}

const FullscreenControl = ({ fullscreenRef }: IFullscreenControl) => {
	const [isFull, setIsFull] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (document.addEventListener) {
			document.addEventListener("fullscreenchange", exitHandler);
			document.addEventListener("mozfullscreenchange", exitHandler);
			document.addEventListener("msfullscreenchange", exitHandler);
			document.addEventListener("webkitfullscreenchange", exitHandler);
		}

		return () => {
			if (document.removeEventListener) {
				document.removeEventListener("fullscreenchange", exitHandler);
				document.removeEventListener("mozfullscreenchange", exitHandler);
				document.removeEventListener("msfullscreenchange", exitHandler);
				document.removeEventListener("webkitfullscreenchange", exitHandler);
			}
		};
	}, []);

	const docWithFullscreenElement = document as Document & {
		mozFullScreenElement: Element;
		webkitFullscreenElement: Element;
		msFullscreenElement: Element;
	};

	function exitHandler() {
		if (!docWithFullscreenElement.fullscreenElement &&
			!docWithFullscreenElement.mozFullScreenElement &&
			!docWithFullscreenElement.webkitFullscreenElement &&
			!docWithFullscreenElement.msFullscreenElement) {
			setIsFull(false);
		}
	}

	const onClickFullscreen = (event: any) => {
		!event.preventDefault && event.preventDefault();
		isFull ? closeFullscreen() : openFullscreen();
		setIsFull(!isFull);
	};

	const openFullscreen = (): void => {
		if (fullscreenRef.current === null) {
			return;
		}

		type TDocElement = HTMLDivElement & {
			mozRequestFullScreen(): Promise<void>;
			webkitRequestFullscreen(): Promise<void>;
			msRequestFullscreen(): Promise<void>;
		};

		const docElmWithBrowsersFullScreenFunctions = fullscreenRef.current as TDocElement;

		if (docElmWithBrowsersFullScreenFunctions.requestFullscreen) {
			void docElmWithBrowsersFullScreenFunctions.requestFullscreen();
		} else if (docElmWithBrowsersFullScreenFunctions.mozRequestFullScreen) { /* Firefox */
			void docElmWithBrowsersFullScreenFunctions.mozRequestFullScreen();
		} else if (docElmWithBrowsersFullScreenFunctions.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
			void docElmWithBrowsersFullScreenFunctions.webkitRequestFullscreen();
		} else if (docElmWithBrowsersFullScreenFunctions.msRequestFullscreen) { /* IE/Edge */
			void docElmWithBrowsersFullScreenFunctions.msRequestFullscreen();
		}
	};

	const closeFullscreen = (): void => {
		type TDocElement = Document & {
			mozCancelFullScreen(): Promise<void>;
			webkitExitFullscreen(): Promise<void>;
			msExitFullscreen(): Promise<void>;
		};

		const docWithBrowsersExitFunctions = document as TDocElement;
		if (docWithBrowsersExitFunctions.exitFullscreen) {
			void docWithBrowsersExitFunctions.exitFullscreen();
		} else if (docWithBrowsersExitFunctions.mozCancelFullScreen) { /* Firefox */
			void docWithBrowsersExitFunctions.mozCancelFullScreen();
		} else if (docWithBrowsersExitFunctions.webkitExitFullscreen) { /* Chrome, Safari and Opera */
			void docWithBrowsersExitFunctions.webkitExitFullscreen();
		} else if (docWithBrowsersExitFunctions.msExitFullscreen) { /* IE/Edge */
			void docWithBrowsersExitFunctions.msExitFullscreen();
		}
	};

	return (
		<div className={s.fullscreenControl}>
			<div className={s.fullscreenControl__button}>
				<img alt={isFull ? "screen" : "fullscreen"} src={isFull ? Assets.Compress : Assets.Expand}
					onClick={onClickFullscreen} />
			</div>
		</div>
	);
};

export default FullscreenControl;
